
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Signal',
  title: 'Reporting returns and decisions',
  description: 'Improve accuracy by reporting outcomes'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`It's critical that you report ACH activity (both decisions and returns) back to Plaid - reporting enables Plaid to help you tune your rule logic to optimize your integration. `}</p>
    <p>{`For both decisions and returns, you can use API endpoints (recommended) or upload the transactions manually via the Signal Dashboard.`}</p>
    <p>{`For Signal Transaction Scores customers, reporting decisions and returns is required in order to receive the most accurate results. For Balance-only customers, it is optional but recommended, as it's necessary in order to view transaction outcomes and tune rule logic in the Dashboard, and will "future proof" your integration if you later choose to augment Balance with Signal Transaction Scores.`}</p>
    <h4 {...{
      "id": "reporting-decisions"
    }}>{`Reporting decisions`}</h4>
    <p>{`Reporting a decision allows Plaid to know that the transaction was processed. Once it knows the transaction was processed, if Plaid doesn't hear about a corresponding return, it assumes the transaction was successful.`}</p>
    <p>{`Plaid will automatically infer decisions for customers using rules with only `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` or `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{` results. There is no need to report decisions back to Plaid for these transactions unless the decision has changed (for example, your processor refused to process an `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`ed transaction).`}</p>
    <p>{`If your rules integration uses `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{` results, then you'll need to call `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{` after you've made the final determination for the transaction.`}</p>
    <p>{`If your integration is directly reading scores and attributes instead of using rules, then you'll need to call `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{` for all transactions.`}</p>
    <h4 {...{
      "id": "reporting-returns"
    }}>{`Reporting returns`}</h4>
    <p>{`To report an ACH return to Plaid, call `}<inlineCode parentName="p">{`/signal/return/report`}</inlineCode>{` as soon as you're aware of the ACH return and it's convenient to do so.`}</p>
    <h4 {...{
      "id": "dashboard-upload"
    }}>{`Dashboard upload`}</h4>
    <p>{`If you would prefer to not report decisions and returns via API, you can upload them via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signal/import"
      }}>{`data import`}</a>{` page in the Dashboard. It is recommended you upload reports on a weekly basis.`}</p>
    <p>{`The CSV files for uploading decisions and returns must follow the schemas defined below. Ensure your data is properly formatted to avoid processing errors.`}</p>
    <Layout.FullWidth>
      <h5 {...{
        "id": "decision-file-csv-schema"
      }}>{`Decision file CSV schema`}</h5>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Column Name`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Required?`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Data Type`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`client_transaction_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Unique identifier for the transaction used in `}<inlineCode parentName="td">{`/signal/evaluate`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`initiated`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Boolean`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Whether the transaction was initiated for processing or not.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`days_funds_on_hold`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Integer`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Number of days the funds were held before being released, if applicable to your use case.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`decision_outcome`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The outcome of your decision for the transaction. One of `}<inlineCode parentName="td">{`APPROVE`}</inlineCode>{`, `}<inlineCode parentName="td">{`NOT_EVALUATED`}</inlineCode>{`, `}<inlineCode parentName="td">{`REJECT`}</inlineCode>{`, `}<inlineCode parentName="td">{`REVIEW`}</inlineCode>{`, `}<inlineCode parentName="td">{`TAKE_OTHER_RISK_MEASURES`}</inlineCode>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`payment_method`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The payment method used for the transaction. One of `}<inlineCode parentName="td">{`SAME_DAY_ACH`}</inlineCode>{`, `}<inlineCode parentName="td">{`NEXT_DAY_ACH`}</inlineCode>{`, `}<inlineCode parentName="td">{`STANDARD_ACH`}</inlineCode>{`, `}<inlineCode parentName="td">{`REAL_TIME_PAYMENTS`}</inlineCode>{`, `}<inlineCode parentName="td">{`DEBIT_CARD`}</inlineCode>{`, `}<inlineCode parentName="td">{`MULTIPLE_PAYMENT_METHODS`}</inlineCode>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`amount_instantly_available`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Number`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The amount made available instantly in US dollars, if relevant for your account funding use case`}</td>
          </tr>
        </tbody>
      </table>
      <p><a parentName="p" {...{
          "href": "https://plaid.com/documents/signal-decision-import-example.csv"
        }}>{`Example decision file CSV`}</a></p>
      <h5 {...{
        "id": "return-file-csv-schema"
      }}>{`Return file CSV schema`}</h5>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Column Name`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Required?`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Data Type`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`client_transaction_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Unique identifier for the transaction used in `}<inlineCode parentName="td">{`/signal/evaluate`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`return_code`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The ACH return code (e.g., R01, R02, R10)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`returned_at`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`String`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Timestamp of when the return occurred in ISO 8601 format (YYYY-MM-DDThh:mm:ssTZD)`}</td>
          </tr>
        </tbody>
      </table>
      <p><a parentName="p" {...{
          "href": "https://plaid.com/documents/signal-return-import-example.csv"
        }}>{`Example return file CSV`}</a></p>
    </Layout.FullWidth>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "reporting-decisions",
        level: 4,
        title: "Reporting decisions",
      },
{
        id: "reporting-returns",
        level: 4,
        title: "Reporting returns",
      },
{
        id: "dashboard-upload",
        level: 4,
        title: "Dashboard upload",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
